import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Collapse,
	CssBaseline,
	InputAdornment,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import Lottie from "lottie-react";
import { Helmet } from "react-helmet-async";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { url } from "src/Helpers/utils/constants";
import Toaster from "src/Toaster/Toaster";
import { LottiedefaultOptions } from "src/Utils/Combined";
import image from "../../../assets/image/bg-login.png";

interface IForgetPasswordInput {
	email: string;
}

const ForgetPassword = () => {
	const Toast = Toaster();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const defaultOptions = LottiedefaultOptions;
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<IForgetPasswordInput>();

	const onSubmit: SubmitHandler<IForgetPasswordInput> = async (data) => {
		setIsLoading(true);
		setErrorMessage("");
		try {
			const res = await axios.post(`${url}/api/common/send-email-otp`, {
				email: data.email,
				type: "forget_admin",
			});

			if (res.data?.success) {
				Toast.fire({
					icon: "success",
					title: `We sent verification code to ${data.email}`,
				});

				setIsLoading(false);
				navigate(`/forget-password/otp?email=${encodeURIComponent(data.email)}`);
			} else {
				const message =
					res.data?.message ||
					"Failed to send verification code. Please check your email.";
				setErrorMessage(message);
				Toast.fire({
					icon: "error",
					title: message,
				});
				setIsLoading(false);
			}
		} catch (err: any) {
			setIsLoading(false);
			const errorMsg =
				err?.response?.data?.message ||
				err?.message ||
				"Failed to send verification code. Please check your email.";
			setErrorMessage(errorMsg);
			Toast.fire({
				icon: "error",
				title: errorMsg,
			});
		}
	};

	return (
		<>
			<Helmet>
				<title>BTTF - Forgot Password</title>
			</Helmet>
			<Box
				sx={{
					minHeight: "100vh",
					width: "100%",
					backgroundImage: `url(${image})`,
					backgroundRepeat: "no-repeat",
					backgroundPosition: "center",
					backgroundSize: "cover",
					backgroundAttachment: "fixed",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					p: { xs: 2, sm: 3 },
				}}
			>
				<CssBaseline />
				<Paper
					elevation={0}
					sx={{
						width: "100%",
						maxWidth: 390,
						borderRadius: "20px",
						p: { xs: 2.5, sm: 3.25 },
						background:
							"linear-gradient(145deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 255, 255, 0.88) 100%)",
						backdropFilter: "blur(24px)",
						WebkitBackdropFilter: "blur(24px)",
						border: "1px solid rgba(255, 255, 255, 0.9)",
						boxShadow:
							"0 18px 40px -10px rgba(0, 0, 0, 0.22), 0 0 0 1px rgba(255, 255, 255, 0.6), inset 0 1px 1px rgba(255, 255, 255, 1)",
						transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
					}}
				>
					{/* Header with Lottie and Title */}
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							mb: 2,
						}}
					>
						<Box
							sx={{
								width: 90,
								height: 90,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								mb: 1,
								borderRadius: "50%",
								background:
									"radial-gradient(circle, rgba(12, 141, 45, 0.1) 0%, rgba(255, 255, 255, 0) 70%)",
							}}
						>
							<Lottie
								style={{ width: "100%", height: "100%" }}
								animationData={defaultOptions?.animationData}
								loop={true}
								autoplay={true}
							/>
						</Box>

						<Typography
							variant="h4"
							sx={{
								fontWeight: 750,
								fontSize: { xs: "1.35rem", sm: "1.5rem" },
								color: "#0f172a",
								textAlign: "center",
								letterSpacing: "-0.025em",
								lineHeight: 1.2,
							}}
						>
							Forgot Password
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: "#64748b",
								textAlign: "center",
								mt: 0.4,
								fontSize: "0.8rem",
								fontWeight: 400,
							}}
						>
							Enter your email to receive a password reset OTP
						</Typography>
					</Box>

					{/* Error Alert if request failed */}
					<Collapse in={!!errorMessage}>
						<Alert
							severity="error"
							onClose={() => setErrorMessage("")}
							sx={{
								mb: 1.5,
								borderRadius: "10px",
								fontSize: "0.8rem",
								py: 0.2,
								"& .MuiAlert-icon": {
									fontSize: "1.1rem",
									alignItems: "center",
								},
							}}
						>
							{errorMessage}
						</Alert>
					</Collapse>

					{/* Form */}
					<Box
						component="form"
						noValidate
						onSubmit={handleSubmit(onSubmit)}
					>
						<TextField
							size="small"
							margin="dense"
							fullWidth
							id="email"
							label="Email Address"
							placeholder="admin@example.com"
							autoComplete="email"
							autoFocus
							disabled={isLoading}
							error={!!errors.email}
							helperText={errors.email?.message}
							{...register("email", {
								required: "Email is required",
								pattern: {
									value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
									message: "Please enter a valid email address",
								},
								onChange: () => {
									if (errorMessage) setErrorMessage("");
								},
							})}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<EmailOutlinedIcon
											sx={{
												color: errors.email
													? "error.main"
													: "#0C8D2D",
												fontSize: 18,
											}}
										/>
									</InputAdornment>
								),
							}}
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: "10px",
									backgroundColor: "rgba(248, 250, 252, 0.85)",
									transition: "all 0.2s ease-in-out",
									"& input": {
										py: 1,
										fontSize: "0.875rem",
									},
									"& fieldset": {
										borderColor: "rgba(203, 213, 225, 0.8)",
									},
									"&:hover fieldset": {
										borderColor: "#94a3b8",
									},
									"&.Mui-focused": {
										backgroundColor: "#ffffff",
										"& fieldset": {
											borderColor: "#0C8D2D",
											borderWidth: "1.5px",
										},
										boxShadow:
											"0 0 0 3px rgba(12, 141, 45, 0.12)",
									},
								},
								"& .MuiInputLabel-root": {
									color: "#64748b",
									fontSize: "0.875rem",
									"&.Mui-focused": {
										color: "#0C8D2D",
										fontWeight: 600,
									},
								},
								"& .MuiFormHelperText-root": {
									fontSize: "0.75rem",
									mt: 0.3,
								},
							}}
						/>

						{/* Submit Button with Loading State */}
						<Button
							type="submit"
							fullWidth
							variant="contained"
							disabled={isLoading}
							startIcon={
								isLoading ? (
									<CircularProgress
										size={18}
										color="inherit"
									/>
								) : (
									<SendIcon sx={{ fontSize: 18 }} />
								)
							}
							sx={{
								mt: 2.2,
								mb: 1.5,
								py: 0.95,
								fontSize: "0.925rem",
								fontWeight: 700,
								letterSpacing: "0.2px",
								textTransform: "none",
								borderRadius: "10px",
								background:
									"linear-gradient(135deg, #0C8D2D 0%, #065F1E 100%)",
								boxShadow:
									"0 8px 18px -3px rgba(12, 141, 45, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
								transition:
									"all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
								"&:hover": {
									background:
										"linear-gradient(135deg, #0a7726 0%, #054e18 100%)",
									boxShadow:
										"0 10px 22px -3px rgba(12, 141, 45, 0.52), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
									transform: "translateY(-1px)",
								},
								"&:active": {
									transform: "translateY(0)",
									boxShadow:
										"0 4px 10px -2px rgba(12, 141, 45, 0.4)",
								},
								"&.Mui-disabled": {
									color: "rgba(255, 255, 255, 0.9)",
									background:
										"linear-gradient(135deg, #0C8D2D 0%, #065F1E 100%)",
									opacity: 0.75,
								},
							}}
						>
							<span>
								{isLoading ? "Sending OTP..." : "Send Verification Code"}
							</span>
						</Button>

						{/* Back to Login Link */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								mt: 1.5,
								mb: 0.5,
							}}
						>
							<Link
								to="/login"
								style={{
									textDecoration: "none",
									display: "inline-flex",
									alignItems: "center",
									gap: "6px",
								}}
							>
								<ArrowBackIcon
									sx={{
										fontSize: 16,
										color: "#0C8D2D",
									}}
								/>
								<Typography
									variant="body2"
									sx={{
										color: "#0C8D2D",
										fontWeight: 600,
										fontSize: "0.825rem",
										transition: "all 0.2s ease",
										"&:hover": {
											color: "#065F1E",
											textDecoration: "underline",
										},
									}}
								>
									Back to Login
								</Typography>
							</Link>
						</Box>

						{/* Footer Note */}
						<Typography
							variant="caption"
							display="block"
							textAlign="center"
							sx={{
								color: "#94a3b8",
								mt: 2,
								fontSize: "0.725rem",
								fontWeight: 500,
								letterSpacing: "0.2px",
							}}
						>
							TOAB • Tour Operators Association of Bangladesh
						</Typography>
					</Box>
				</Paper>
			</Box>
		</>
	);
};

export default ForgetPassword;

